import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { ButtonGroupOption } from '../../../core/models/common/input-option';
import { fromStore } from '../../../core/store';
import { map, take } from 'rxjs/operators';
import {
  BookingsDisplayMode,
  BookingsScope,
} from '../../../core/store/bookings/bookings.state';
import { addDays, formatISO, startOfToday } from 'date-fns';
import { combineLatest, firstValueFrom, Observable, takeUntil } from 'rxjs';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { BookingPermission } from '../../../core/models/entities/permission';

@Component({
  selector: 'app-bookings-list',
  template: `
    <div class="rounded-lg border border-black p-4 xl:p-8">
      <div class="space-y-4 xl:flex xl:space-y-0 xl:space-x-4">
        <div class="xl:w-96">
          <app-search-input
            data-cy="bookings-search"
            [placeholder]="searchInputPlaceholder"
            [searchText]="searchText$ | async"
            [marginTop]="false"
            [marginBottom]="false"
            (searchChanged)="searchChanged($event)"
          ></app-search-input>
        </div>
    
        <div
          class="space-y-4 md:flex md:items-center md:justify-between md:space-y-0 xl:contents"
          >
          @if ((displayMode$ | async) === BookingsDisplayMode.List) {
            <div class="min-w-72">
              <app-date-input
                [control]="datesRangeControl"
                [flatpickrOptions]="{ mode: 'range' }"
                placeholder="Filtrer par dates"
                [marginTop]="false"
                [marginBottom]="false"
              ></app-date-input>
            </div>
          } @else {
            <div>
              <div class="flex items-center space-x-4">
                @if (displayMode$ | async; as displayMode) {
                  <div
                    class="flex items-center"
                    >
                    <button
                      data-cy="previous-button"
                    (click)="
                      displayMode === BookingsDisplayMode.Week
                        ? incrementDate(-7)
                        : incrementDate(-1)
                    "
                      class="h-11 px-4"
                      ><i class="fas fa-chevron-left"></i
                    ></button>
                    <div class="w-56 flex-none md:w-72">
                      <app-date-input
                      [dateFormat]="
                        (displayMode$ | async) === BookingsDisplayMode.Day
                          ? 'EEE d LLL yyyy'
                          : 'd LLL yyyy'
                      "
                        [control]="datesControl"
                        [marginTop]="false"
                        [marginBottom]="false"
                      [weekSelection]="
                        (displayMode$ | async) === BookingsDisplayMode.Week
                      "
                        variant="light"
                        name="dates"
                      ></app-date-input>
                    </div>
                    <button
                      data-cy="next-button"
                    (click)="
                      displayMode === BookingsDisplayMode.Week
                        ? incrementDate(7)
                        : incrementDate(1)
                    "
                      class="h-11 px-4"
                      ><i class="fas fa-chevron-right"></i
                    ></button>
                  </div>
                }
                <div class="hidden xl:block">
                  <app-button
                    variant="outlined"
                    size="sm"
                    data-cy="today-button"
                    (click)="datesControl.patchValue([today])"
                    >Aujourd'hui</app-button
                    >
                  </div>
                </div>
              </div>
            }
    
            <div class="xl:ml-auto">
              @if (displayModeControl) {
                <app-button-group
                  [control]="displayModeControl"
                  [options]="displayModeOptions()"
                ></app-button-group>
              }
            </div>
          </div>
        </div>
    
        @if (displayMode$ | async; as displayMode) {
          <div>
            @if (displayMode !== BookingsDisplayMode.List) {
              <app-bookings-calendar-view
                [scope]="scope"
              ></app-bookings-calendar-view>
            }
            @if (displayMode === BookingsDisplayMode.List) {
              <app-spacer size="6"></app-spacer>
              <app-bookings-list-view [scope]="scope"></app-bookings-list-view>
            }
          </div>
        }
      </div>
    `,
  standalone: false,
})
export class BookingsListComponent
  extends DestroyableComponent
  implements OnInit
{
  @Input() scope: BookingsScope;
  datesControl: UntypedFormControl;
  datesRangeControl: UntypedFormControl = new UntypedFormControl();
  displayModeControl: UntypedFormControl;
  displayModeOptions = signal<ButtonGroupOption[]>([
    { label: 'Semaine', value: BookingsDisplayMode.Week },
    { label: 'Jour', value: BookingsDisplayMode.Day },
    { label: 'Liste', value: BookingsDisplayMode.List },
  ]);

  displayMode$: Observable<BookingsDisplayMode>;
  searchText$: Observable<string>;
  calendarDate$: Observable<Date>;
  calendarDate: Signal<Date>;
  BookingsDisplayMode = BookingsDisplayMode;
  today = startOfToday();
  protected readonly BookingPermission = BookingPermission;

  constructor(
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();

    /**
     * Si écran <= md on retire l'option "Semaine" car pas assez de place pour l'afficher
     */
    afterNextRender(() => {
      const mql = window.matchMedia('(max-width: 768px)');
      this.updateDisplayModeOptions(mql.matches);

      mql.addEventListener('change', (e) => {
        this.updateDisplayModeOptions(e.matches);
      });
    });
  }

  /**
   * Met à jour les options d'affichage selon la taille d'écran
   */
  private updateDisplayModeOptions(isMobile: boolean) {
    if (isMobile) {
      this.displayModeOptions.set([
        { label: 'Jour', value: BookingsDisplayMode.Day },
        { label: 'Liste', value: BookingsDisplayMode.List },
      ]);
    } else {
      this.displayModeOptions.set([
        { label: 'Semaine', value: BookingsDisplayMode.Week },
        { label: 'Jour', value: BookingsDisplayMode.Day },
        { label: 'Liste', value: BookingsDisplayMode.List },
      ]);
    }
  }

  ngOnInit() {
    this.calendarDate = this.store.selectSignal(
      fromStore.selectBookingsCalendarDate(this.scope),
    );

    this.displayMode$ = this.store.select(
      fromStore.selectBookingsDisplayMode(this.scope),
    );
    this.searchText$ = combineLatest([
      this.displayMode$,
      this.store.select(fromStore.selectBookingsSearch(this.scope)),
    ]).pipe(
      map(([displayMode, searchText]) =>
        displayMode === BookingsDisplayMode.List ? searchText : '',
      ),
    );
    this.calendarDate$ = this.store.select(
      fromStore.selectBookingsCalendarDate(this.scope),
    );

    // Setup new display mode control
    this.displayMode$.pipe(take(1)).subscribe((displayMode) => {
      this.displayModeControl = new UntypedFormControl(displayMode);
      this.displayModeControl.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => {
          this.store.dispatch(
            bookingsActions.setDisplayMode({
              displayMode: value,
              scope: this.scope,
            }),
          );
        });
    });

    this.store.dispatch(
      bookingsActions.findAll({ page: 1, scope: this.scope }),
    );
    this.store
      .select(fromStore.selectBookingsState)
      .pipe(take(1))
      .subscribe((bookingsState) => {
        /*this.viewModeControl = new UntypedFormControl(
          bookingsState[this.scope].viewMode,
        );*/
        this.datesControl = new UntypedFormControl(
          bookingsState[this.scope].calendarDate,
          [Validators.required],
        );
        this.datesRangeControl = new UntypedFormControl(
          bookingsState[this.scope].from && bookingsState[this.scope].to
            ? [bookingsState[this.scope].from, bookingsState[this.scope].to]
            : null,
        );
        this.changeDetectorRef.detectChanges();
      });
    this.datesControl.valueChanges.subscribe((dates) => {
      if (this.calendarDate()?.valueOf() !== dates[0]?.valueOf()) {
        this.store.dispatch(
          bookingsActions.setCalendarDate({
            date: dates[0],
            scope: this.scope,
          }),
        );
      }
    });
    this.datesRangeControl.valueChanges.subscribe((dates) => {
      if (dates.length !== 1) {
        this.store.dispatch(
          bookingsActions.setDatesRange({
            dates: dates?.map((date) => formatISO(date)),
            scope: this.scope,
          }),
        );
      }
    });
    // Sync display mode with changes
    this.displayMode$.subscribe((displayMode) => {
      this.displayModeControl?.setValue(displayMode);
    });

    this.calendarDate$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((date) => this.datesControl.patchValue([date]));
  }

  incrementDate(increment: number) {
    this.calendarDate$.pipe(take(1)).subscribe((date) => {
      this.store.dispatch(
        bookingsActions.setCalendarDate({
          date: addDays(date, increment),
          scope: this.scope,
        }),
      );
    });
  }

  async filtersChanged(filter) {
    const displayMode: BookingsDisplayMode = await firstValueFrom(
      this.displayMode$,
    );
    if (displayMode === BookingsDisplayMode.List) {
      this.store.dispatch(
        bookingsActions.setBookingsFilter({ filter, scope: this.scope }),
      );
    } else {
      this.store.dispatch(
        bookingsActions.setRoomsFilter({ filter, scope: this.scope }),
      );
    }
  }

  searchChanged(text) {
    this.store.dispatch(
      bookingsActions.setSearch({ search: text, scope: this.scope }),
    );
  }

  get searchInputPlaceholder() {
    return this.scope === BookingsScope.All
      ? 'Rechercher par no de rés. ou nom du client'
      : 'Rechercher par no de rés. ou intitulé';
  }
}
